import React from 'react'
import { useTranslation } from 'react-i18next'
import { Icon } from '../../atoms/Icon'
import { Loading } from '../../atoms/Loading'
import { Source } from '../../atoms/Source'
import { AnalysisSectionHeader } from '../../AnalysisSectionHeader'
import { NoiseProps, NoiseSource } from '@popety-io/types'
import {
  NoiseContainer,
  NoiseOverview,
  NoiseCard,
  NoiseCardHeader,
  TimeIndicator,
  TimeLabel,
  NoiseValueDisplay,
  NoiseValue,
  NoiseUnit,
  NoiseCardBody,
  NoiseDescription,
  NoiseSources,
  NoiseSource as NoiseSourceStyled,
  SourceIcon,
  SourceLabel,
  SourceValue,
  DecibelScaleCard,
  ScaleTitle,
  VisualScaleIntegrated,
  ScaleGradient,
  ScaleLabels,
  ScaleLabelItem,
  DbValue,
  DbDesc,
  ScaleIndicators,
  Indicator,
  IndicatorLine,
  IndicatorLabel,
  ScaleReferenceGrid,
  ReferenceColumn,
  ReferenceItem,
  RefValue,
  RefExample,
  NoiseLoadingContainer,
  NoiseErrorContainer,
  NoiseErrorMessage,
  NoiseErrorDetails,
  NoiseSourcesContainer,
} from './NoiseAnalysis.styles'

const getNoiseLevel = (db: number): 'low' | 'moderate' | 'high' => {
  if (db >= 70) return 'high'
  if (db >= 50) return 'moderate'
  return 'low'
}

const convertApiDataToSources = (data: any, t: any): NoiseSource[] => {
  const sources: NoiseSource[] = []

  // Road traffic
  const roadDay = data?.road_day?.max || 0
  const roadNight = data?.road_night?.max || 0
  if (roadDay > 0 || roadNight > 0) {
    sources.push({
      type: 'road',
      label: t('sources.roadTraffic'),
      maxDayValue: roadDay,
      maxNightValue: roadNight,
      avgDayValue: data?.road_day?.avg || 0,
      avgNightValue: data?.road_night?.avg || 0,
      icon: 'car',
    })
  }

  // Railway
  const railwayDay = data?.railway_day?.max || 0
  const railwayNight = data?.railway_night?.max || 0
  if (railwayDay > 0 || railwayNight > 0) {
    sources.push({
      type: 'railway',
      label: t('sources.railway'),
      maxDayValue: railwayDay,
      maxNightValue: railwayNight,
      avgDayValue: data?.railway_day?.avg || 0,
      avgNightValue: data?.railway_night?.avg || 0,
      icon: 'map', // Using map icon as fallback since train might not exist
    })
  }

  return sources
}

const NoiseSourceItem: React.FC<{
  source: NoiseSource
  period: 'day' | 'night'
  t: any
}> = ({ source, period, t }) => {
  const value =
    period === 'day' ? source.maxDayValue || 0 : source.maxNightValue || 0
  const level = getNoiseLevel(value)

  return (
    <NoiseSourceStyled>
      <SourceIcon>
        <Icon name={source.icon as any} size="small" />
      </SourceIcon>
      <SourceLabel>{source.label}</SourceLabel>
      <SourceValue $level={level}>
        {value > 0 ? `${Math.round(value)} ${t('unit')}` : `0 ${t('unit')}`}
      </SourceValue>
    </NoiseSourceStyled>
  )
}

const NoiseCardComponent: React.FC<{
  period: 'day' | 'night'
  value: number
  sources: NoiseSource[]
  t: any
}> = ({ period, value, sources, t }) => {
  const timeIcon = period === 'day' ? 'sun' : 'clock' // Using clock icon as fallback since moon might not exist
  const description = t(`descriptions.${period}`)

  return (
    <NoiseCard $period={period}>
      <NoiseCardHeader>
        <TimeIndicator $period={period}>
          <Icon name={timeIcon as any} size="medium" />
          <TimeLabel>{t(`periods.${period}`)}</TimeLabel>
        </TimeIndicator>
        <NoiseValueDisplay>
          <NoiseValue $period={period}>{Math.round(value)}</NoiseValue>
          <NoiseUnit>{t('unit')}</NoiseUnit>
        </NoiseValueDisplay>
      </NoiseCardHeader>
      <NoiseCardBody>
        <NoiseDescription>{description}</NoiseDescription>
        <NoiseSources>
          {sources.map((source, index) => (
            <NoiseSourceItem
              key={`${source.type}-${index}`}
              source={source}
              period={period}
              t={t}
            />
          ))}
        </NoiseSources>
      </NoiseCardBody>
    </NoiseCard>
  )
}

const DecibelScale: React.FC<{
  dayLevel: number
  nightLevel: number
  t: any
}> = ({ dayLevel, nightLevel, t }) => {
  // Calculate positions as percentages (0-100 scale)
  const dayPosition = Math.min(Math.max(dayLevel, 0), 100)
  const nightPosition = Math.min(Math.max(nightLevel, 0), 100)
  // Elevate night label only when too close to day label (< 15% apart)
  const tooClose = Math.abs(dayPosition - nightPosition) < 15

  const scaleLabels = [
    { value: 0, label: t('scale.levels.silent') },
    { value: 20, label: t('scale.levels.quiet') },
    { value: 40, label: t('scale.levels.moderate') },
    { value: 60, label: t('scale.levels.loud') },
    { value: 80, label: t('scale.levels.veryLoud') },
    { value: 100, label: t('scale.levels.harmful') },
  ]

  const referenceItems = [
    {
      range: t('scale.ranges.0-20'),
      example: t('scale.examples.breathing'),
    },
    {
      range: t('scale.ranges.30-40'),
      example: t('scale.examples.whispering'),
    },
    {
      range: t('scale.ranges.50-60'),
      example: t('scale.examples.conversation'),
    },
    {
      range: t('scale.ranges.70-80'),
      example: t('scale.examples.traffic'),
      highlight: true,
    },
    {
      range: t('scale.ranges.90-100'),
      example: t('scale.examples.lawnmower'),
    },
    {
      range: t('scale.ranges.40+'),
      example: t('scale.examples.sleepInterference'),
    },
  ]

  return (
    <DecibelScaleCard>
      <ScaleTitle>{t('scale.title')}</ScaleTitle>

      <VisualScaleIntegrated>
        <ScaleGradient />
        <ScaleLabels>
          {scaleLabels.map((item, index) => (
            <ScaleLabelItem key={index} $left={`${item.value}%`}>
              <DbValue>{item.value} dB</DbValue>
              <DbDesc>{item.label}</DbDesc>
            </ScaleLabelItem>
          ))}
        </ScaleLabels>
        <ScaleIndicators>
          <Indicator $left={`${dayPosition}%`} $period="day">
            <IndicatorLine $period="day" />
            <IndicatorLabel $period="day">
              {t('scale.indicators.day', { value: Math.round(dayLevel) })}
            </IndicatorLabel>
          </Indicator>
          <Indicator
            $left={`${nightPosition}%`}
            $period="night"
            $elevated={tooClose}
          >
            <IndicatorLine $period="night" />
            <IndicatorLabel $period="night">
              {t('scale.indicators.night', {
                value: Math.round(nightLevel),
              })}
            </IndicatorLabel>
          </Indicator>
        </ScaleIndicators>
      </VisualScaleIntegrated>

      <ScaleReferenceGrid>
        <ReferenceColumn>
          {referenceItems.slice(0, 3).map((item, index) => (
            <ReferenceItem key={index} $highlight={item.highlight}>
              <RefValue>{item.range}</RefValue>
              <RefExample>{item.example}</RefExample>
            </ReferenceItem>
          ))}
        </ReferenceColumn>
        <ReferenceColumn>
          {referenceItems.slice(3).map((item, index) => (
            <ReferenceItem key={index + 3} $highlight={item.highlight}>
              <RefValue>{item.range}</RefValue>
              <RefExample>{item.example}</RefExample>
            </ReferenceItem>
          ))}
        </ReferenceColumn>
      </ScaleReferenceGrid>
    </DecibelScaleCard>
  )
}

/**
 * NoiseAnalysis component displays noise pollution data for a location.
 * Features include:
 * - Day and night noise levels
 * - Noise sources breakdown (road traffic, railway)
 * - Decibel scale reference with visual indicators
 * - Data source attribution
 *
 * @param data - Noise statistics data from API
 * @param loading - Loading state
 * @param error - Error message if data fetch failed
 * @param className - Optional CSS class name
 * @param showDataSources - Whether to show data sources footer
 */
export const NoiseAnalysis: React.FC<NoiseProps> = ({
  data,
  loading = false,
  error = null,
  className,
  showDataSources = true,
}) => {
  const { t } = useTranslation('noise')

  if (loading) {
    return (
      <NoiseLoadingContainer>
        <Loading size="large" />
        <NoiseErrorDetails>{t('states.loading')}</NoiseErrorDetails>
      </NoiseLoadingContainer>
    )
  }

  if (error) {
    return (
      <NoiseErrorContainer>
        <NoiseErrorMessage>{t('states.error')}</NoiseErrorMessage>
        <NoiseErrorDetails>{error}</NoiseErrorDetails>
      </NoiseErrorContainer>
    )
  }

  if (!data) {
    return (
      <NoiseErrorContainer>
        <NoiseErrorMessage>{t('states.noData')}</NoiseErrorMessage>
        <NoiseErrorDetails>{t('states.noDataDescription')}</NoiseErrorDetails>
      </NoiseErrorContainer>
    )
  }

  // Calculate max noise levels for day and night
  const dayLevel = Math.max(data.road_day?.max || 0, data.railway_day?.max || 0)
  const nightLevel = Math.max(
    data.road_night?.max || 0,
    data.railway_night?.max || 0
  )

  const sources = convertApiDataToSources(data, t)

  return (
    <NoiseContainer className={className}>
      <AnalysisSectionHeader title={t('title')} subtitle={t('subtitle')} />

      <NoiseOverview>
        <NoiseCardComponent
          period="day"
          value={dayLevel}
          sources={sources}
          t={t}
        />
        <NoiseCardComponent
          period="night"
          value={nightLevel}
          sources={sources}
          t={t}
        />
      </NoiseOverview>

      <DecibelScale dayLevel={dayLevel} nightLevel={nightLevel} t={t} />

      {showDataSources && (
        <NoiseSourcesContainer>
          <Source
            sources={[
              t('dataSources.sitg'),
              t('dataSources.geneva'),
              t('dataSources.federal'),
            ]}
          />
        </NoiseSourcesContainer>
      )}
    </NoiseContainer>
  )
}

export default NoiseAnalysis
